import { useEffect, useRef, useState } from 'react';
import { fetchAwareImageObjectUrl } from 'utils/awareImageUtils';

const useImageObjectUrlCache = (imageUrls = []) => {
  const [imageMap, setImageMap] = useState(new Map());
  const imageCacheRef = useRef(new Map());

  useEffect(() => {
    let isMounted = true; // prevents state updates if unmounted
    const cache = imageCacheRef.current;

    // Clears cache and state if no image URLs provided
    if (!imageUrls.length) {
      cache.forEach((objectUrl) => URL.revokeObjectURL(objectUrl)); // remove from browser memory
      imageCacheRef.current = new Map(); // reset cache
      setImageMap(new Map()); // reset state
      return;
    }

    const loadImages = async () => {
      const urlsToLoad = imageUrls.filter((url) => !cache.has(url));
      const urlsToRemove = Array.from(cache.keys()).filter((url) => !imageUrls.includes(url));

      if (!urlsToLoad.length && !urlsToRemove.length) return; // nothing to update

      // Loads images in batches
      const loadedEntries = await Promise.all(
        urlsToLoad.map(async (url) => [url, await fetchAwareImageObjectUrl(url)]),
      );

      loadedEntries.forEach(([url, objectUrl]) => {
        if (objectUrl) {
          cache.set(url, objectUrl);
        }
      });

      urlsToRemove.forEach((url) => {
        const objectUrl = cache.get(url);
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        cache.delete(url);
      });

      if (!isMounted) return; // prevent state update if unmounted

      setImageMap(new Map(cache));
    };

    loadImages();

    return () => {
      isMounted = false;
    };
  }, [imageUrls.join('|')]); // dependency on joined string to avoid array reference issues

  // Keep seperate cleanup effect to clear cache on unmount since React runs cleanup on rerun of main effect
  useEffect(() => {
    return () => {
      imageCacheRef.current.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
    };
  }, []);

  return imageMap;
};

export default useImageObjectUrlCache;
